import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle } from 'lucide-react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore, orgTypeFor } from '@/stores/auth.store';
import { useSocketStore } from '@/stores/socket.store';
import { getAuthLoginUrl } from '@/lib/auth-callback';
import {
  clearLoginRedirects,
  isLoginLooping,
  recordLoginRedirect,
  reportLoginLoop,
  storageAcceptsWrites,
} from '@/lib/auth-loop-guard';
import { IS_STANDALONE } from '@/config/standalone';
import { ROUTE_CONFIG, EmbeddedLayout } from '@/config/routes.config';
import { renderRoutes } from '@/lib/renderRoutes';
import { identityApi } from '@/lib/api';
import { IdentityConflictScreen } from '@/components/auth/IdentityConflictScreen';

/**
 * Extract and store auth tokens from URL hash (for dev login flow)
 * This runs SYNCHRONOUSLY before the app renders
 */
function processUrlHashAuth(): boolean {
  const hash = window.location.hash;
  if (!hash || !hash.includes('access_token')) return false;

  // Parse the hash fragment
  const params = new URLSearchParams(hash.substring(1));
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');

  if (accessToken) {
    console.log('[App] Found access token in URL hash, storing...');

    // Store in the format app-rail expects (and zustand persist format)
    const authData = {
      state: {
        accessToken,
        refreshToken,
        isAuthenticated: true,
      },
      version: 0,
    };
    localStorage.setItem('solid-hub-auth', JSON.stringify(authData));

    // Clear the hash from the URL (security + cleaner URL)
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    console.log('[App] Token stored, URL hash cleared');
    return true;
  }
  return false;
}

// Process URL hash BEFORE any React code runs
const tokenWasInUrl = processUrlHashAuth();
import { AuthCallback } from '@/pages/AuthCallback';
import { Toaster } from '@/components/ui/Toaster';
import { useAppBarItems } from '@/components/layout/AppBarItems';

// Declare SolidHub global type
declare global {
  interface Window {
    SolidHub?: {
      init: () => Promise<void>;
      isInitialized: () => boolean;
      getUser: () => any;
      getAccessToken: () => Promise<string | null>;
      logout: () => Promise<void>;
      getLoginUrl: (redirectUrl?: string) => string;
      extensions: {
        registerBottomNav: (item: {
          id: string;
          label: string;
          icon?: string;
          badge?: { count: number; variant?: 'default' | 'danger' | 'warning' };
          order?: number;
          onClick?: () => void;
          href?: string;
          tooltip?: string;
        }) => () => void;
        updateBadge: (itemId: string, badge: { count: number } | null) => void;
      };
    };
  }
}

interface AppProps {
  /**
   * When true, the app is running as a micro-frontend inside the shell.
   * This hides the AppRail since the shell provides it.
   */
  isEmbedded?: boolean;
}

/**
 * The first thing anybody sees, while the app rail brokers auth.
 *
 * It is the page GROUND, so it has to be the ecosystem's grey-50 — the same
 * colour the shell paints once the app mounts. It was navy, left over from the
 * hub's dark era, which meant every cold load flashed dark and then snapped to
 * light the instant the shell rendered.
 */
function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-grey-50">
      <div className="text-center">
        <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-accent" aria-hidden />
        <p className="text-body-sm text-grey-500" role="status">
          Loading…
        </p>
      </div>
    </div>
  );
}

/**
 * Hook to wait for SolidHub to be initialized and authenticated
 */
function useAppRailAuth() {
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');
  const [user, setUser] = useState<any>(null);
  const { setAuth, setUser: setStoreUser } = useAuthStore();

  useEffect(() => {
    let mounted = true;
    // eslint-disable-next-line prefer-const
    let checkInterval: NodeJS.Timeout;

    // Standalone dev mode: when running hub-web without the app-bar
    // micro-frontend, there is no window.SolidHub to broker auth. Trust hub's
    // own auth store (populated by the OIDC callback) instead. Gated by
    // VITE_HUB_STANDALONE so production always requires the app-bar.
    if (IS_STANDALONE) {
      const localAuth = useAuthStore.getState();
      if (localAuth.isAuthenticated && localAuth.accessToken) {
        setUser(localAuth.user);
        setStatus('authenticated');
      } else {
        setStatus('unauthenticated');
      }
      return () => { mounted = false; };
    }

    const checkAppRail = async () => {
      // Wait for SolidHub to exist and be initialized
      if (!window.SolidHub) {
        return; // Keep waiting
      }

      if (!window.SolidHub.isInitialized()) {
        return; // Keep waiting
      }

      // Hub-rail is initialized - check if we have a user
      const hubUser = window.SolidHub.getUser();
      
      if (hubUser && mounted) {
        // User is authenticated via app-rail
        const token = await window.SolidHub.getAccessToken();

        // Transform user object to match auth store interface
        // Auth service returns orgs: [{ org_id, name, slug, role, type }]
        // Auth store expects organizations: [{ id, name, slug, role, type }]
        // Note: hubUser has `sub` (OIDC standard) but auth store expects `id`
        // Note: hubUser has `picture` (OIDC standard) but auth store expects `avatarUrl`
        const transformedUser = {
          ...hubUser,
          id: hubUser.sub || hubUser.id, // Map OIDC `sub` claim to `id`
          avatarUrl: hubUser.picture, // Map OIDC `picture` claim to `avatarUrl`
          organizations: hubUser.orgs?.map((o: any) => ({
            id: o.org_id,
            name: o.name,
            slug: o.slug,
            role: o.role,
            // The claim carries `kind`, never `type`: defaulting to 'AGENCY'
            // made every org look like Solid Digital to every web check.
            type: orgTypeFor(o.org_id, o.kind),
            kind: o.kind,
          })) || [],
        };

        // Sync to local auth store for API calls
        if (token) {
          setAuth(token, transformedUser);
          setStoreUser(transformedUser);
        }

        setUser(transformedUser);
        setStatus('authenticated');
        clearInterval(checkInterval);
      } else if (mounted) {
        // Hub-rail initialized but no user means redirect happened or will happen
        // The app-rail handles the redirect, so we just show loading
        setStatus('unauthenticated');
        clearInterval(checkInterval);
      }
    };

    // Check immediately
    checkAppRail();

    // Then poll every 100ms until app-rail is ready
    checkInterval = setInterval(checkAppRail, 100);

    // Timeout after 15 seconds - only if still mounted and no status change happened
    const timeout = setTimeout(() => {
      if (mounted) {
        // Use a flag instead of checking status (which is stale in this closure)
        setStatus(currentStatus => {
          if (currentStatus === 'loading') {
            console.error('[App] Hub-rail initialization timeout');
            return 'unauthenticated';
          }
          return currentStatus;
        });
      }
    }, 15000);

    return () => {
      mounted = false;
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [setAuth, setStoreUser]);

  return { status, user };
}

function AuthenticatedApp({ isEmbedded }: { isEmbedded: boolean }) {
  const { connect, disconnect } = useSocketStore();
  const queryClient = useQueryClient();

  /**
   * Someone whose auth account was recreated arrives with a new id while the
   * Hub still holds the profile their work hangs off — so the Hub would open
   * empty. Ask which profile is theirs before showing anything, rather than
   * guessing: a reassigned email address looks identical from here (see the
   * API's IdentityConflictService).
   */
  const { data: identity, isLoading: identityLoading } = useQuery({
    queryKey: ['identity-conflict'],
    queryFn: () => identityApi.getConflict(),
    staleTime: Infinity,
    retry: false,
  });

  // Connect WebSocket when app mounts
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  // Favorites and Activity on the app bar. Support and Settings are NOT on
  // it any more: they're at the bottom of the left nav (Jesse, 2026-10-07).
  useAppBarItems();

  // Hold the app back until the profile question is answered.
  if (identityLoading) return <LoadingScreen />;
  if (identity?.conflict) {
    return (
      <IdentityConflictScreen
        conflict={identity.conflict}
        onResolved={() => {
          queryClient.clear();
          window.location.href = window.location.origin;
        }}
      />
    );
  }

  return (
    <>
      <Routes>
        {renderRoutes(ROUTE_CONFIG, { layoutOverride: isEmbedded ? EmbeddedLayout : undefined })}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </>
  );
}

/**
 * Check if we're on the OAuth callback page
 */
function isCallbackPage(): boolean {
  return window.location.pathname === '/auth/callback' &&
    window.location.search.includes('code=');
}

/**
 * Sends the user to the login page — exactly once per page load.
 *
 * The latch is module-level rather than component state because assigning
 * window.location.href a second time CANCELS the navigation already in flight.
 * Without it, any re-render of this branch restarts the redirect, and the
 * browser can never finish reaching the auth server.
 */
let loginRedirectStarted = false;

function RedirectingToLogin() {
  const [looping, setLooping] = useState(() => isLoginLooping());

  useEffect(() => {
    if (loginRedirectStarted) return;
    loginRedirectStarted = true;

    // A login round trip that lands back here means the tokens we were just
    // issued did not survive the trip. Redirecting again would only repeat it.
    const attempts = recordLoginRedirect();

    // Report from the second attempt on, not only once we give up: if the
    // counter is being reset by something we haven't found yet, the give-up
    // branch may never be reached and we would learn nothing.
    if (attempts >= 2) reportLoginLoop(attempts);

    if (attempts > 3) {
      setLooping(true);
      return;
    }

    getAuthLoginUrl(window.location.href).then((url) => {
      window.location.href = url;
    });
  }, []);

  if (looping) return <AuthLoopError />;
  return <LoadingScreen />;
}

/**
 * Shown when sign-in keeps completing successfully but never sticks. Names the
 * most likely cause instead of bouncing silently, so the user has something
 * they can act on and support has something to go on.
 */
function AuthLoopError() {
  const storageBroken = !storageAcceptsWrites();

  return (
    <div className="flex min-h-screen items-center justify-center bg-grey-50 p-6">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
          <AlertCircle className="h-6 w-6 text-error" aria-hidden />
        </div>
        <h1 className="mb-3 text-heading-3 text-secondary">We couldn't keep you signed in</h1>
        <p className="mb-6 text-body-sm text-grey-500">
          {storageBroken
            ? 'Your browser is blocking this site from saving data, so your sign-in is discarded as soon as the page reloads. Allow site data for hub.soliddigital.com — or try a normal (non-private) window, or a different browser.'
            : 'Sign-in completed but did not persist. This is usually caused by private browsing, a browser extension, or a network filter clearing site data. Try a normal window, or a different browser or network.'}
        </p>
        <button
          onClick={() => {
            clearLoginRedirects();
            window.location.href = window.location.origin;
          }}
          className="btn-primary"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export default function App({ isEmbedded = false }: AppProps) {
  const { status } = useAppRailAuth();

  // Only a session that STAYS authenticated proves the round trip worked.
  // Clearing the moment 'authenticated' first renders is wrong: a loop that
  // flickers through an authenticated state on every iteration would reset the
  // counter each time and the guard could never trip, however long it ran.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const settled = setTimeout(clearLoginRedirects, 10_000);
    return () => clearTimeout(settled);
  }, [status]);

  // If we're on the auth callback page with a code, render it directly
  // without requiring authentication (app-rail will process the callback)
  if (isCallbackPage()) {
    return <AuthCallback />;
  }

  // Show loading while app-rail initializes and checks auth
  if (status === 'loading') {
    return <LoadingScreen />;
  }

  // If unauthenticated, redirect to login
  // Hub-rail doesn't handle redirects - the host app does
  if (status === 'unauthenticated') {
    return <RedirectingToLogin />;
  }

  // User is authenticated - render the app
  return <AuthenticatedApp isEmbedded={isEmbedded} />;
}
